import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../hooks/usePayment';
import posthog from 'posthog-js';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface BalanceScreenProps {
  userInfo: UserInfo;
}

interface PaymentOption {
  stars: number;
  sparkles: number;
  isPopular?: boolean;
}

const PAYMENT_OPTIONS: PaymentOption[] = [
  { stars: 100, sparkles: 100 },
  { stars: 200, sparkles: 200},
  { stars: 500, sparkles: 500, isPopular: true },
  { stars: 1000, sparkles: 1000 },
];

export function BalanceScreen({ userInfo }: BalanceScreenProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState(() => 
    PAYMENT_OPTIONS.findIndex(option => option.isPopular) || 0
  );

  const { isLoading, handleBalancePayment } = usePayment({
    onPaymentComplete: (updatedUserInfo) => {
      // Update local state with new balance before navigating back
      userInfo.balance = updatedUserInfo.balance;
      // Navigate back after successful payment
      navigate(-1);
    }
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handlePayClick = () => {
    const selectedPaymentOption = PAYMENT_OPTIONS[selectedOption];
    posthog.capture('add_balance_payment_clicked', { amount_stars: selectedPaymentOption.stars, amount_sparkles: selectedPaymentOption.sparkles });
    handleBalancePayment(selectedPaymentOption.stars);
  };

  return (
    <div className="min-h-screen bg-theme-bg">
      <div className="fixed top-0 left-0 right-0 z-[100] shadow-[0_4px_12px_-6px_rgba(0,0,0,0.1)]" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleBack}
              className="p-2 -ml-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
            >
              <span className="material-symbols-outlined text-theme-text">arrow_back</span>
            </button>
            <h1 className="text-2xl font-bold text-theme-text/90">
              {t('balanceScreen.title')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-[88px]">
        <div className="text-center mb-8">
          <div className="text-xl font-medium text-theme-text mb-2">
            {t('balanceScreen.subtitle')}
          </div>
          <div className="text-theme-hint mb-4">
            {t('balanceScreen.rateInfo')}
          </div>
          <div className="flex items-center justify-center gap-2 text-lg">
            <span>{t('balanceScreen.currentBalance')}</span>
            <div className="flex items-center gap-1.5 text-[#2D62FF] font-medium">
              <span className="text-xl">✨</span>
              <span>{userInfo.balance}</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {PAYMENT_OPTIONS.map((option, index) => (
            <button
              key={index}
              onClick={() => setSelectedOption(index)}
              className={`w-full p-4 rounded-2xl border transition-all ${
                index === selectedOption
                  ? 'border-[#2D62FF] bg-[#2D62FF]/10 hover:bg-[#2D62FF]/15' 
                  : 'border-white/10 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⭐️</span>
                  <span className="font-medium text-theme-text">{t('balanceScreen.starsAmount', { stars: option.stars })}</span>
                </div>
                <div className="flex items-center gap-2">
                  {option.isPopular && (
                    <span className="text-xs font-medium text-[#2D62FF] bg-[#2D62FF]/10 px-2 py-1 rounded-full">
                      {t('balanceScreen.popularOption')}
                    </span>
                  )}
                  {index === selectedOption && (
                    <span className="material-symbols-outlined text-[#2D62FF]">check_circle</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-theme-hint text-sm">
                <span className="text-base">✨</span>
                <span>{t('balanceScreen.getSparkles', { sparkles: option.sparkles })}</span>
              </div>
            </button>
          ))}
        </div>

        <button 
          onClick={handlePayClick}
          disabled={isLoading}
          className="fixed bottom-6 left-4 right-4 mx-auto max-w-2xl bg-[#2D62FF] text-white font-medium py-4 rounded-xl shadow-lg hover:shadow-xl active:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t('balanceScreen.processing')}</span>
            </div>
          ) : (
            t('balanceScreen.payButton')
          )}
        </button>
      </div>
    </div>
  );
} 